import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Shield, 
  Users, 
  Activity, 
  Stethoscope, 
  FileText, 
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Star,
  TrendingUp,
  Clock,
  Globe
} from 'lucide-react';

const LandingPage = () => {
  const features = [
    {
      icon: Shield,
      title: 'Sécurité des données',
      description: 'Vos informations médicales sont protégées et sécurisées'
    },
    {
      icon: Users,
      title: 'Gestion complète',
      description: 'Gérez vos patients et consultations en toute simplicité'
    },
    {
      icon: Activity,
      title: 'Suivi en temps réel',
      description: 'Surveillez les données médicales en temps réel'
    },
    {
      icon: MessageCircle,
      title: 'Communication',
      description: 'Communiquez facilement avec les professionnels de santé'
    },
    {
      icon: FileText,
      title: 'Rapports détaillés',
      description: 'Générez des rapports médicaux complets et précis'
    },
    {
      icon: Stethoscope,
      title: 'Consultations',
      description: 'Planifiez et gérez vos consultations médicales'
    }
  ];

  const benefits = [
    'Accès 24/7 à votre dossier médical',
    'Communication directe avec votre médecin',
    'Historique complet de vos consultations',
    'Prescriptions et médicaments en ligne',
    'Notifications importantes en temps réel'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8 animate-bounce">
              <Heart className="h-10 w-10 text-white" />
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-slide-in-down">
              HealthBridge
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-4 animate-slide-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
              Votre plateforme de gestion médicale intelligente
            </p>
            
            <p className="text-lg text-blue-200 mb-12 max-w-2xl mx-auto animate-slide-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
              Connectez les professionnels de santé et les patients pour une meilleure prise en charge médicale
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-in-up" style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
              <Link
                to="/register-doctor"
                className="group flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:bg-blue-50"
              >
                <Stethoscope className="h-5 w-5 mr-2" />
                Inscription Médecin
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link
                to="/register-patient"
                className="group flex items-center justify-center px-8 py-4 bg-green-500 text-white rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:bg-green-600"
              >
                <Users className="h-5 w-5 mr-2" />
                Inscription Patient
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="mt-8 animate-fade-in" style={{ animationDelay: '0.8s', animationFillMode: 'both' }}>
              <Link
                to="/login"
                className="text-blue-200 hover:text-white underline text-sm transition-colors"
              >
                Vous avez déjà un compte ? Se connecter
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Fonctionnalités principales
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez tous les outils dont vous avez besoin pour une gestion médicale optimale
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100 animate-slide-in-up"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'both' }}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Pourquoi choisir HealthBridge ?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Une solution complète pour moderniser la gestion de votre santé et améliorer la communication entre patients et professionnels de santé.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 animate-slide-in-left"
                    style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'both' }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-6 shadow-md transform hover:scale-105 transition-transform">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">1000+</div>
                    <div className="text-gray-600">Patients actifs</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-6 shadow-md transform hover:scale-105 transition-transform">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                      <Stethoscope className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">500+</div>
                    <div className="text-gray-600">Médecins</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-6 shadow-md transform hover:scale-105 transition-transform">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                      <Activity className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">24/7</div>
                    <div className="text-gray-600">Disponibilité</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-6 shadow-md transform hover:scale-105 transition-transform">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                      <Star className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">4.9/5</div>
                    <div className="text-gray-600">Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à commencer ?
            </h2>
            <p className="text-xl text-blue-100 mb-12">
              Rejoignez HealthBridge aujourd'hui et transformez votre expérience médicale
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register-doctor"
                className="group flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:bg-blue-50"
              >
                <Stethoscope className="h-5 w-5 mr-2" />
                Inscription Médecin
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link
                to="/register-patient"
                className="group flex items-center justify-center px-8 py-4 bg-green-500 text-white rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:bg-green-600"
              >
                <Users className="h-5 w-5 mr-2" />
                Inscription Patient
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Heart className="h-6 w-6 text-red-500" />
                <span className="text-xl font-bold">HealthBridge</span>
              </div>
              <p className="text-gray-400">
                Votre plateforme de gestion médicale intelligente
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Navigation</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/login" className="hover:text-white transition-colors">
                    Connexion
                  </Link>
                </li>
                <li>
                  <Link to="/register-doctor" className="hover:text-white transition-colors">
                    Inscription Médecin
                  </Link>
                </li>
                <li>
                  <Link to="/register-patient" className="hover:text-white transition-colors">
                    Inscription Patient
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Fonctionnalités</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="hover:text-white transition-colors cursor-pointer">Gestion des patients</li>
                <li className="hover:text-white transition-colors cursor-pointer">Consultations</li>
                <li className="hover:text-white transition-colors cursor-pointer">Chat en temps réel</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Email: contact@healthbridge.cd</li>
                <li>Téléphone: +243 XXX XXX XXX</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 HealthBridge. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

